import React, { useMemo } from 'react';

import { DateTime } from 'luxon';

import { Table, Text } from '@shared/components/bootstrap';

import { Label } from '@admin/components/helpers/label';
import { Panel } from '@admin/components/helpers/panel';
import { UsersDetailsQuery } from '@admin/schema';

type UserSkill = NonNullable<UsersDetailsQuery['user']>['skills'][0];
type UserSkillTraining = NonNullable<UsersDetailsQuery['user']>['skillTrainings'][0];

type RowStatus = 'ACTIVE' | 'REVOKED' | 'IN_TRAINING';

type LabelKind = 'success' | 'muted' | 'info';

interface SkillRow {
  kind: string;
  displayName: string;
  assignment?: UserSkill;
  status: RowStatus;
}

const STATUS_LABEL: Record<RowStatus, LabelKind> = {
  ACTIVE: 'success',
  REVOKED: 'muted',
  IN_TRAINING: 'info',
};

const STATUS_SORT_RANK: Record<RowStatus, number> = {
  ACTIVE: 3,
  IN_TRAINING: 2,
  REVOKED: 1,
};

const formatDate = (iso: string | null | undefined) =>
  iso ? DateTime.fromISO(iso).toLocaleString(DateTime.DATE_MED) : '—';

const formatStatus = (status: RowStatus) => {
  if (status === 'IN_TRAINING') return 'In Training';
  if (status === 'REVOKED') return 'Inactive';
  return status.charAt(0) + status.slice(1).toLowerCase();
};

const buildSkillRows = (skills: UserSkill[], skillTrainings: UserSkillTraining[]): SkillRow[] => {
  const assignmentsByKind = new Map(skills.map((skill) => [skill.skill.kind, skill]));
  const inProgressByKind = new Map(
    skillTrainings.filter((training) => training.inProgress).map((training) => [training.skill.kind, training]),
  );

  const kinds = new Set<string>();
  skills.forEach((skill) => kinds.add(skill.skill.kind));
  inProgressByKind.forEach((_training, kind) => kinds.add(kind));

  return Array.from(kinds)
    .map((kind) => {
      const assignment = assignmentsByKind.get(kind);
      const inProgressTraining = inProgressByKind.get(kind);
      const skillMeta = assignment?.skill ?? inProgressTraining?.skill;
      const status: RowStatus = inProgressTraining
        ? 'IN_TRAINING'
        : ((assignment?.status as Exclude<RowStatus, 'IN_TRAINING'>) ?? 'REVOKED');

      return {
        kind,
        displayName: skillMeta?.displayName ?? kind,
        assignment,
        status,
      };
    })
    .sort((left, right) => {
      const statusDiff = STATUS_SORT_RANK[right.status] - STATUS_SORT_RANK[left.status];
      if (statusDiff !== 0) return statusDiff;
      return left.displayName.localeCompare(right.displayName);
    });
};

export const UserSkillsPanel: React.FC<{ skills: UserSkill[]; skillTrainings: UserSkillTraining[] }> = ({
  skills,
  skillTrainings,
}) => {
  const rows = useMemo(() => buildSkillRows(skills, skillTrainings), [skills, skillTrainings]);

  return (
    <Panel>
      <Panel.Header>
        <Panel.Title>Qualifications</Panel.Title>
      </Panel.Header>
      <Panel.Body>
        {rows.length === 0 ? (
          <Text tag="p" style="muted">
            No qualifications assigned.
          </Text>
        ) : (
          <Table responsive striped>
            <thead>
              <tr>
                <th>Qualification</th>
                <th>Status</th>
                <th>Granted</th>
                <th>Inactive</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.kind}>
                  <td>{row.displayName}</td>
                  <td>
                    <Label kind={STATUS_LABEL[row.status]}>{formatStatus(row.status)}</Label>
                  </td>
                  <td>{formatDate(row.assignment?.createdAt)}</td>
                  <td>{formatDate(row.assignment?.revokedAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Panel.Body>
    </Panel>
  );
};
